import { useEffect, useMemo, useRef, useState } from 'react';
import { usePaymentByExternalIdLive } from '@repo/domain-service';
import { TestId } from '@repo/test-id';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { trackPayNowReturn } from '@features/reservation/trackers/trackPayNowReturn';
import { type PaymentResultLocationState } from '@features/reservation/types';
import { takeSpanPaymentLinkRedirect } from '@features/reservation/utils/spanPaymentLinkRedirect';

import PleaseWaitAnimation from '@shared/components/PleaseWaitAnimation';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface Props {
  success?: boolean;
}

const payNowStatuses: Record<string, boolean> = {
  ABANDONED: false,
  CONFIRMED: true, // the only positive status
  ERROR: false,
  EXPIRED: false,
  NEW: false,
  PENDING: false,
  REJECTED: false,
};

/** How long to wait for the backend to settle a still-pending payment before trusting the URL status. */
const PENDING_PAYMENT_WAIT_MILLISECONDS = 60_000;

/**
 * Examplary page load URL
 * https://test.parkapp.pl/payment/success?paymentId=PJSF-H6X-PMD-ZXL
 * https://test.parkapp.pl/payment/success?paymentId=PJSF-H6X-PMD-ZXL&paymentStatus=CONFIRMED
 * https://test.parkapp.pl/payment/failure?paymentId=PJSF-H6X-PMD-ZXL&paymentStatus=REJECTED
 *
 * The same return URL serves every product, so the payment is looked up by the
 * gateway's `paymentId` to learn what was paid for and its settled status; the
 * result pages then show content for that product. Without a match — no session,
 * lookup failure, or still pending after the wait — the URL status decides and
 * the result pages assume a reservation, as before.
 */
export default function PaymentPayNowStatus({ success }: Props) {
  const testId = useMemo<TestId>(() => TestId.root('reservation.payNowStatus'), []);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [waitTimedOut, setWaitTimedOut] = useState<boolean>(false);
  const handledRef = useRef<boolean>(false);

  const paymentStatus: string = searchParams.get('paymentStatus') ?? (success ? 'CONFIRMED' : '');
  const paymentId: string = searchParams.get('paymentId') ?? '';
  const isPaymentStatusKnown: boolean = (payNowStatuses[paymentStatus] ?? undefined) !== undefined;
  const isUrlPaymentSuccessful: boolean = payNowStatuses[paymentStatus] ?? false;
  const { payment, loadingPayment, paymentLookupFailed } = usePaymentByExternalIdLive(paymentId || undefined);

  const isPaymentSettled = payment !== undefined && payment.status !== 'pending';
  const isPaymentUnresolvable = !paymentId || paymentLookupFailed || (!loadingPayment && !payment) || waitTimedOut;
  const isReadyToLeave = isPaymentSettled || isPaymentUnresolvable;
  const isPaymentSuccessful = isPaymentSettled ? payment.status === 'succeeded' : isUrlPaymentSuccessful;
  const orderProduct = payment?.orderProduct;

  useEffect(() => {
    trackPayNowReturn({ isPaymentStatusKnown, paymentStatus, paymentId });
    // Track the return once, on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const waitTimeout = setTimeout(() => setWaitTimedOut(true), PENDING_PAYMENT_WAIT_MILLISECONDS);
    return () => clearTimeout(waitTimeout);
  }, []);

  useEffect(() => {
    if (!isReadyToLeave || handledRef.current) return;
    handledRef.current = true;

    // A payment made through the payment link ends on its own result pages, which show nothing of the reservation.
    if (takeSpanPaymentLinkRedirect()) {
      const linkResultRoute = isPaymentSuccessful
        ? RouteName.RESERVATION_SPAN_PAYMENT_LINK_SUCCESS
        : RouteName.RESERVATION_SPAN_PAYMENT_LINK_FAILURE;
      navigate(ROUTE_PATHS[linkResultRoute], { replace: true });
      return;
    }

    const resultRoute = isPaymentSuccessful ? RouteName.RESERVATION_SUCCESS : RouteName.RESERVATION_FAILURE;
    const state: PaymentResultLocationState = { product: orderProduct ?? 'parkingSpotRental' };
    navigate(ROUTE_PATHS[resultRoute], { replace: true, state });
  }, [isReadyToLeave, isPaymentSuccessful, orderProduct, navigate]);

  return <PleaseWaitAnimation testId={testId} />;
}
